import { useRef, useState } from "react";
import { Mail, Globe, Send, Upload, FileText, X } from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { SITE } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const MAX_TOTAL = 15 * 1024 * 1024; // 15 MB
const ACCEPT = ".pdf,.doc,.docx,.txt,.rtf,.png,.jpg,.jpeg";

function fmtSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function Contact() {
  useDocumentTitle("Contact Meta61 | Start a conversation");
  const fileInputRef = useRef(null);
  const [status, setStatus] = useState(null); // null | "sending" | "ok" | "error"
  const [errorMsg, setErrorMsg] = useState("");
  const [files, setFiles] = useState([]);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    company: "",
    phone: "",
    message: "",
  });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const addFiles = (incoming) => {
    const next = [...files, ...incoming];
    const total = next.reduce((sum, f) => sum + f.size, 0);
    if (total > MAX_TOTAL) {
      setErrorMsg("Attachments exceed the 15 MB total limit.");
      setStatus("error");
      return;
    }
    setErrorMsg("");
    setStatus(null);
    setFiles(next);
  };

  const onFilePick = (e) => {
    if (e.target.files?.length) addFiles(Array.from(e.target.files));
    e.target.value = "";
  };

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const onSubmit = async () => {
    if (!form.full_name || !form.email || !form.message) {
      setErrorMsg("Please complete the required fields (name, email and message).");
      setStatus("error");
      return;
    }
    setStatus("sending");
    setErrorMsg("");
    try {
      const body = new FormData();
      body.append("full_name", form.full_name);
      body.append("email", form.email);
      body.append("company", form.company);
      body.append("phone", form.phone);
      body.append("subject", "Website enquiry");
      body.append("message", form.message);
      files.forEach((f) => body.append("files", f));
      const res = await fetch(`${API}/contact`, { method: "POST", body });
      if (res.ok) {
        setStatus("ok");
      } else {
        const data = await res.json().catch(() => ({}));
        setErrorMsg(data.detail || "Something went wrong. Please try again or email us directly.");
        setStatus("error");
      }
    } catch (err) {
      setErrorMsg("Network error. Please try again or email us directly.");
      setStatus("error");
    }
  };

  const inputClass =
    "w-full bg-m61-bg border border-m61-line rounded-lg px-4 py-3 text-m61-text placeholder:text-m61-muted/60 focus:border-m61-cyan focus:outline-none transition-colors";
  const labelClass = "block text-sm text-m61-text mb-2 font-medium";

  return (
    <section className="m61-section" data-testid="contact-page">
      <div className="m61-container">
        <p className="m61-eyebrow mb-6">Contact</p>
        <h1 className="m61-h1 mb-4">
          Start a <span className="m61-gradient-text">conversation.</span>
        </h1>
        <p className="m61-lead max-w-2xl mb-14">
          Tell us what you are working towards and we will show you how these technologies come together.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Form */}
          <div className="lg:col-span-7">
            <h2 className="m61-h3 mb-8">Send us a Message</h2>

            {status === "ok" ? (
              <div className="m61-card p-10 text-center" data-testid="contact-success">
                <div className="w-14 h-14 rounded-full mx-auto mb-5 flex items-center justify-center"
                  style={{ background: "var(--m61-grad)" }}>
                  <Send className="w-6 h-6 text-m61-bg" />
                </div>
                <h3 className="m61-h3 mb-3">Thank you.</h3>
                <p className="text-m61-muted">
                  Your message has been received and a member of the team will be in touch.
                </p>
              </div>
            ) : (
              <div className="m61-card p-6 lg:p-8 space-y-6" data-testid="contact-form">
                <div>
                  <label className={labelClass} htmlFor="full_name">Full Name *</label>
                  <input id="full_name" name="full_name" value={form.full_name} onChange={onChange}
                    className={inputClass} placeholder="Your name" data-testid="contact-name-input" />
                </div>
                <div>
                  <label className={labelClass} htmlFor="email">Email *</label>
                  <input id="email" name="email" type="email" value={form.email} onChange={onChange}
                    className={inputClass} placeholder="you@example.com" data-testid="contact-email-input" />
                </div>
                <div>
                  <label className={labelClass} htmlFor="company">Company</label>
                  <input id="company" name="company" value={form.company} onChange={onChange}
                    className={inputClass} placeholder="Your organisation" data-testid="contact-company-input" />
                </div>
                <div>
                  <label className={labelClass} htmlFor="phone">Phone</label>
                  <input id="phone" name="phone" value={form.phone} onChange={onChange}
                    className={inputClass} placeholder="Optional" data-testid="contact-phone-input" />
                </div>
                <div>
                  <label className={labelClass} htmlFor="message">Message *</label>
                  <textarea id="message" name="message" rows={6} value={form.message} onChange={onChange}
                    className={`${inputClass} resize-y`} placeholder="What are you working towards?"
                    data-testid="contact-message-input" />
                </div>

                {/* Attachments */}
                <div>
                  <label className={labelClass}>Attachments</label>
                  <p className="text-sm text-m61-muted mb-3">Upload supporting documents if needed (max 15 MB total)</p>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full border border-dashed border-m61-line-strong rounded-lg py-8 px-4 flex flex-col items-center gap-2 text-m61-muted hover:border-m61-cyan hover:text-m61-cyan transition-colors"
                    data-testid="contact-upload-trigger"
                  >
                    <Upload className="w-6 h-6" />
                    <span className="text-sm font-medium">Click to upload files</span>
                    <span className="text-xs font-mono uppercase tracking-wider text-m61-muted/70">
                      PDF, DOC, DOCX, TXT, RTF, PNG, JPG accepted
                    </span>
                  </button>
                  <input ref={fileInputRef} type="file" multiple accept={ACCEPT} onChange={onFilePick}
                    className="hidden" data-testid="contact-file-input" />

                  {files.length > 0 && (
                    <ul className="mt-4 space-y-2" data-testid="contact-file-list">
                      {files.map((f, i) => (
                        <li key={`${f.name}-${i}`}
                          className="flex items-center justify-between gap-3 bg-m61-bg border border-m61-line rounded-lg px-3 py-2">
                          <span className="flex items-center gap-2 min-w-0 text-sm text-m61-text">
                            <FileText className="w-4 h-4 text-m61-cyan shrink-0" />
                            <span className="truncate">{f.name}</span>
                            <span className="text-m61-muted shrink-0">({fmtSize(f.size)})</span>
                          </span>
                          <button type="button" onClick={() => removeFile(i)}
                            className="text-m61-muted hover:text-m61-violet transition-colors shrink-0"
                            aria-label={`Remove ${f.name}`} data-testid={`contact-remove-file-${i}`}>
                            <X className="w-4 h-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {status === "error" && (
                  <p className="text-sm text-m61-violet" data-testid="contact-error">{errorMsg}</p>
                )}

                <button onClick={onSubmit} disabled={status === "sending"}
                  className="m61-btn-primary w-full justify-center"
                  data-testid="contact-submit-button">
                  {status === "sending" ? "Sending..." : "Send Message"} <Send className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* Contact Information */}
          <div className="lg:col-span-5">
            <h2 className="m61-h3 mb-8">Contact Information</h2>
            <div className="space-y-5">
              <a href={`mailto:${SITE.contactEmail}`}
                className="m61-card p-6 flex items-start gap-4 group" data-testid="contact-info-email">
                <span className="w-11 h-11 rounded-lg bg-m61-surface2 border border-m61-line flex items-center justify-center shrink-0 group-hover:border-m61-cyan transition-colors">
                  <Mail className="w-5 h-5 text-m61-cyan" />
                </span>
                <div>
                  <p className="text-m61-text font-medium mb-1">Email</p>
                  <p className="text-m61-muted group-hover:text-m61-cyan transition-colors break-all">
                    {SITE.contactEmail}
                  </p>
                </div>
              </a>

              <div className="m61-card p-6 flex items-start gap-4" data-testid="contact-info-global">
                <span className="w-11 h-11 rounded-lg bg-m61-surface2 border border-m61-line flex items-center justify-center shrink-0">
                  <Globe className="w-5 h-5 text-m61-cyan" />
                </span>
                <div>
                  <p className="text-m61-text font-medium mb-1">Global Organisation</p>
                  <p className="text-m61-muted">We operate around the clock</p>
                  <p className="text-m61-muted text-sm">Supporting clients worldwide</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
